import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import CloseIcon from '@mui/icons-material/Close';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';
import { post } from 'api';
import { enqueueSnackbar } from 'notistack';
import { getLocation } from 'services/getLocation';
import * as yup from 'yup';

const enquirySchema = yup.object({
  name: yup.string().required('Customer Name is mandatory'),
  // email: yup
  //   .string()
  //   .trim()
  //   .nullable()
  //   .transform((value) => (value === '' ? null : value))
  //   .email('Invalid email')
  //   .notRequired(),
  email: yup.string().trim().email('Invalid email').notRequired(),
  mobile: yup
    .string()
    .required('Customer Mobile is mandatory')
    .matches(/^\d{10}$/, 'Mobile must be exactly 10 digits'),
  city: yup.string().required('Select City'),
  typeOfProperty: yup.string().required('Select Type Of Property'),
  requirementCapacity: yup
    .number()
    .typeError('Requirement Capacity in kw')
    .required('Requirement Capacity in kw')
    .min(1, 'Requirement Capacity in kw'),
  address: yup.string().required('Address is mandatory'),
  solarType: yup
    .string()
    .oneOf(['DCR', 'NDCR'], 'Solar type must be DCR or NDCR')
    .required('Solar type is required'),
});

export default function EnquiryCreate({ open, setOpen, fetchData }) {
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(enquirySchema),
    defaultValues: {
      name: '',
      email: '',
      mobile: '',
      city: '',
      solarType: '',
      typeOfProperty: '',
      requirementCapacity: 0,
      address: '',
    },
  });
  const [city, setCity] = useState([]);
  const [loading, setLoading] = useState(false);

  const onClose = () => {
    reset();
    setOpen(false);
    fetchData();
    setLoading(false);
  };

  const submitStock = async (data) => {
    try {
      setLoading(true);
      let coordinates = await getLocation(); // Try to get location

      if (!coordinates || coordinates.length !== 2) {
        coordinates = [22.997435, 72.501755];
        // enqueueSnackbar('Unable to get your location. Please enable location access.', {
        //   variant: 'error',
        // });
      }
      data.coordinates = coordinates;
      const res = await post('users/create_enquiry', data);

      if (res.status == 200) {
        enqueueSnackbar(res.message || 'Enquiry Created Successfully', {
          variant: 'success',
          autoHideDuration: 3000,
        });
      } else {
        enqueueSnackbar(res.message || 'Error While Creating Enquiry', {
          variant: 'error',
          autoHideDuration: 3000,
        });
      }
    } catch (error) {
      enqueueSnackbar(error.message || 'Error While Creating Followup Try Again Latter', {
        variant: 'error',
        autoHideDuration: 3000,
      });
    } finally {
      onClose();
    }
  };

  const fetchCity = async () => {
    try {
      const res = await post('users/dropdown_city', { id: '64510906f470354af0a761e6' });
      if (res.status == 200) {
        setCity(res.data);
      } else {
        setCity([]);
      }
    } catch (error) {
      enqueueSnackbar(error.message || 'Error while Fetching City', {
        variant: 'error',
        autoHideDuration: 3000,
      });
    }
  };
  useEffect(() => {
    fetchCity();
  }, []);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      component="form"
      onSubmit={handleSubmit(submitStock)}
    >
      {/* HEADER */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ p: 1 }}>
        <DialogTitle sx={{ fontSize: 20, fontWeight: 700 }}>Create Enquiry</DialogTitle>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Stack>

      {/* BODY */}
      <DialogContent sx={{ px: 2 }}>
        {/* VENDOR */}
        <Grid container spacing={2}>
          <Grid item size={{ xs: 12, sm: 6 }}>
            <Controller
              control={control}
              name="name"
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Customer Name"
                  error={!!errors.name}
                  helperText={errors.name?.message}
                  slotProps={{ htmlInput: { maxLength: 30 } }}
                />
              )}
            />
          </Grid>
          <Grid item size={{ xs: 12, sm: 6 }}>
            <Controller
              control={control}
              name="email"
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Customer Email"
                  error={!!errors.email}
                  helperText={errors.email?.message}
                  slotProps={{ htmlInput: { maxLength: 30 } }}
                />
              )}
            />
          </Grid>
          <Grid item size={{ xs: 12, sm: 6 }}>
            <Controller
              control={control}
              name="mobile"
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Contact Number"
                  error={!!errors.mobile}
                  helperText={errors.mobile?.message}
                  slotProps={{ htmlInput: { maxLength: 15 } }}
                />
              )}
            />
          </Grid>
          <Grid item size={{ xs: 12, sm: 6 }}>
            <Controller
              control={control}
              name="requirementCapacity"
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  type="number"
                  label="Required KW"
                  error={!!errors.requirementCapacity}
                  helperText={errors.requirementCapacity?.message}
                  slotProps={{ htmlInput: { maxLength: 200 } }}
                />
              )}
            />
          </Grid>
          <Grid item size={{ xs: 12, sm: 6 }}>
            <Controller
              name="city"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth error={!!errors.city}>
                  <InputLabel> City</InputLabel>
                  <Select {...field} label="City">
                    {city.map((c) => (
                      <MenuItem key={c._id} value={c._id}>
                        {`${c.name}`}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>{errors.city?.message}</FormHelperText>
                </FormControl>
              )}
            />
          </Grid>
          <Grid item size={{ xs: 12, sm: 6 }}>
            <Controller
              name="typeOfProperty"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth error={!!errors.typeOfProperty}>
                  <InputLabel> Type Of Property</InputLabel>
                  <Select {...field} label="typeOfProperty">
                    {[
                      { _id: 'Commercial', name: 'Commercial' },
                      { _id: 'Flat', name: 'Flat' },
                      { _id: 'Residential', name: 'Residential' },
                      { _id: 'Solar Park', name: 'Solar Park' },
                    ].map((c) => (
                      <MenuItem key={c._id} value={c._id}>
                        {`${c.name}`}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>{errors.reportingManager?.message}</FormHelperText>
                </FormControl>
              )}
            />
          </Grid>
          <Grid item size={{ xs: 12, sm: 6 }}>
            <Controller
              name="solarType"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth error={!!errors.typeOfProperty}>
                  <InputLabel> Solar Type</InputLabel>
                  <Select {...field} label="solarType">
                    {[
                      { _id: 'DCR', name: 'DCR (Domestic Content Requirement)' },
                      { _id: 'NDCR', name: 'NDCR (Non-DCR)' },
                    ].map((c) => (
                      <MenuItem key={c._id} value={c._id}>
                        {`${c.name}`}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>{errors.solarType?.message}</FormHelperText>
                </FormControl>
              )}
            />
          </Grid>

          <Grid item size={{ xs: 12, sm: 12 }}>
            <Controller
              control={control}
              name="address"
              render={({ field }) => (
                <TextField
                  {...field}
                  multiline
                  rows={3}
                  fullWidth
                  label="Address"
                  error={!!errors.address}
                  helperText={errors.address?.message}
                  slotProps={{ htmlInput: { maxLength: 200 } }}
                />
              )}
            />
          </Grid>
        </Grid>
      </DialogContent>

      {/* FOOTER */}
      <DialogActions sx={{ p: 2 }}>
        <Button variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="contained" type="submit" loading={loading}>
          Creat
        </Button>
      </DialogActions>
    </Dialog>
  );
}
